import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useDeviceType } from '@hotjar/webapp/hooks/useDeviceType';
import { useIsDemoUser } from '@hotjar/webapp/hooks/useIsDemoUser';
import { clearUserData } from '@hotjar/webapp/domains/common/user-data';
import type { SignupTrackingData } from '@hotjar/tracking';
import { useRegistrationCampaign } from '@hotjar/webapp/hooks/useCampaigns';
import type { RegistrationResultDto } from '@hotjar/webapp/domains/acquisition/register/dto';
import {
  DEFAULT_PLAN,
  DEFAULT_SSO_PROVIDER,
  REGISTER_STATE,
  REGISTRATION_STEP,
} from '@hotjar/webapp/features/Registration/common/constants';
import { useRegisterQueryParams } from './useRegisterQueryParams';
import type { SignupMethodSettings } from './useRegisterQueryParams';

export interface UserCreationFormTracking {
  trackOnEmailFormSubmit: () => void;
  trackOnDefaultSSOSubmit: () => void;
  trackOnEmailCodeSubmit: (isValid: boolean, method: string) => void;
  trackOnEmailCodeComplete: () => void;
  trackUserCreation: (user: RegistrationResultDto) => Promise<void>;
  trackUserCreationError: (err: Error) => void;
}

export const useUserCreationFormTracking = (
  signupMethod: SignupMethodSettings,
): UserCreationFormTracking => {
  const track = useTrack();
  const isDemoUser = useIsDemoUser();
  const { campaignId, utmContent } = useRegisterQueryParams();
  const { isLoading: isLoadingCampaign, data: campaign } = useRegistrationCampaign(campaignId);
  const { deviceType } = useDeviceType();
  const { i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const trackingData: SignupTrackingData = {
    deviceType,
    signupMethod: signupMethod.method,
    signupStepNumber: 1,
    signupStep: REGISTRATION_STEP,
    signupSitePlan: DEFAULT_PLAN,
    stateEventWasTriggeredOn: REGISTER_STATE,
    ...(currentLocale ? { locale: currentLocale } : {}),
  };

  useEffect(() => {
    if (isDemoUser) {
      track.resetAndKeepAnonymousId();
    } else {
      // If we already identified this user at some point, reset their tracking data
      // so future events are only attributed to a new user.
      track.logoutIfAlreadyIdentified();
      clearUserData();
    }
  }, [isDemoUser, track]);

  useEffect(() => {
    if (utmContent) {
      track.viewElement('register_with_utm_content_param', 'Register', {
        state_event_was_triggered_on: utmContent,
      });
    }
  }, [utmContent, track]);

  useEffect(() => {
    if (!isLoadingCampaign) {
      track.signupStepLoaded({ ...trackingData, signupMethod: signupMethod.method, campaign });
    }
    // we need to catch the point when campaign gets loaded, so subscribing to isLoadingCampaign only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCampaign]);

  const trackOnEmailFormSubmit = () => {
    track.emailVerificationRequest();
    track.signupStepSubmitted({ ...trackingData, signupMethod: signupMethod.method, campaign });
  };

  const trackOnDefaultSSOSubmit = () => {
    track.signupStepSubmitted({ ...trackingData, signupMethod: DEFAULT_SSO_PROVIDER, campaign });
  };

  const trackOnEmailCodeSubmit = (isValid: boolean, method = 'code') => {
    track.emailVerificationAttempted(isValid, method);
  };

  const trackOnEmailCodeComplete = () => {
    track.signupStepCompleted({ ...trackingData, signupMethod: signupMethod.method, campaign });
  };

  const trackUserCreation = (user: RegistrationResultDto) =>
    track
      .userCreated({ submitSuccess: true, plan: trackingData.signupSitePlan as string })
      .then(() => track.alias(user.user_id));

  const trackUserCreationError = (err: Error) => {
    track.userCreatedError(String(err));
  };

  return {
    trackOnEmailFormSubmit,
    trackOnDefaultSSOSubmit,
    trackOnEmailCodeSubmit,
    trackOnEmailCodeComplete,
    trackUserCreation,
    trackUserCreationError,
  };
};
